import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Drawer } from "../../../components/Drawer";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { CreateKanbanDto } from "../../../model/kanban/dto/CreateKanbanDto";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { KanbanTemplates } from "../../../model/kanban/types/KanbanTemplates.enum";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Text } from "../../../components/Text";
import { KanbanTemplatePicker } from "./KanbanTemplatePicker";
import { DrawerDefaultsExtra } from "../../tasks/components/DrawerDefaultsExtra";
import { Info } from "../../../components/Info";
import { Switch } from "../../../utils/local-antd";
import { TestIDs } from "../../../tests/types/testIds";

export const KanbanCreateDrawer: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { kanbanCreateVisible, setKanbanCreateVisible } = useDrawersContext();
  const { control, getValues, setError, reset, handleSubmit, watch } =
    useForm<CreateKanbanDto>({
      defaultValues: {
        name: "",
        template: KanbanTemplates.DEFAULT,
        userId,
        hasClosingColumns: true,
      },
    });
  const hasClosingColumns = watch("hasClosingColumns");

  const localOnClose = () => {
    setKanbanCreateVisible(false);
    reset();
  };

  const onCreate = async () => {
    try {
      setLoading(true);

      const dto = getValues();

      await dispatch(kanbanActions.createKanban(dto)).unwrap();

      await Promise.all([
        dispatch(kanbanActions.getUserKanbansMeta({ userId })).unwrap(),
        dispatch(thresholdActions.getThresholds({ userId })).unwrap(),
      ]);

      AtiraToast.success(t("kanban.create.success"));

      localOnClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={t("kanban.create.title")}
      open={kanbanCreateVisible}
      onClose={localOnClose}
      size="large"
      extra={
        <DrawerDefaultsExtra
          onCancel={localOnClose}
          onSubmit={handleSubmit(onCreate)}
          config={{ loading }}
          submitTestId={TestIDs.kanbans.createEdit.kanban.button}
        />
      }
    >
      <Flex flexDirection="column" gap="l">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.this_field_required"),
            },
            minLength: {
              value: Lengths.MIN_3,
              message: t("error.length.more", {
                length: 3,
                name: t("kanban.create.input.title"),
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              onChange={onChange}
              value={value}
              title={t("kanban.create.input.title")}
              valid={!error}
              errorMessage={error?.message}
              required
              info={t("kanban.create.input.info")}
              dataTestId={TestIDs.kanbans.createEdit.kanban.name}
            />
          )}
        />

        <Controller
          control={control}
          name="hasClosingColumns"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Flex justifyContent="center" gap="s" flexDirection="column">
              <Flex gap="s" alignItems="center">
                <Text>{t("kanban.create.defaultColumns.title")}</Text>
                <Switch
                  onChange={onChange}
                  checked={hasClosingColumns}
                  className={TestIDs.kanbans.createEdit.kanban.defaultColumns}
                />
              </Flex>
              <Info info={t("kanban.create.defaultColumns.info")} />
            </Flex>
          )}
        />

        <Controller
          control={control}
          name="template"
          render={({ field: { value, onChange } }) => (
            <Flex flexDirection="column" gap="s">
              <Text fontSize="m">{t("common.template")}</Text>

              <Info
                info={
                  <Trans
                    i18nKey={"kanban.create.template.info"}
                    components={[<br />]}
                  />
                }
              />

              <KanbanTemplatePicker
                value={value || KanbanTemplates.DEFAULT}
                onChange={onChange}
                hasClosingColumns={hasClosingColumns}
              />
            </Flex>
          )}
        />
      </Flex>
    </Drawer>
  );
};
